import React, { useEffect, useState } from 'react'
import IssuesApp, { EmptyIssueModel, IIssuesModal } from './IssuesApp'
import BasicDataViewer, { IBasicDataViewItem } from '../../components/DataDisplayer/BasicDataViewer'
import { useDispatch } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { updateGlobalLoaderStatus, updateQuickNotificationDetails } from '../notifications/notificationsSlice'
import { usePageDetails } from '../admin/adminPagesSlice'
import { handleRequestError } from '../../services/api'
import PageHeadSection from '../admin/components/PageHeadSection'
import { replaceItemDataInLink } from '../BaseApp'
import SectionBoxContainer from '../../components/SectionBoxContainer'
import NotFound from '../../components/NotFound'
import RenderBlockEditor, { IBlock } from '../../components/Fields/RenderBlockEditor'
import { Block } from 'typescript'
import Tabs from '../../components/Tabs'
import GenerateIcon from '../../components/GenerateIcon'
import AlertElement from '../../components/AlertElement'
import RequestDesignation from '../items_requests/RequestDesignation'
import RequestDestination from '../items_requests/RequestDestination'
import { EmptyItemRequestModel } from '../items_requests/ItemsRequestsApp'
import Attachments from '../../components/Attachments'

interface IProps {
    customId?: number,
    hidePageHeadSection?: boolean
}

export default function ViewSingleIssuePage(props: IProps) {

    const { customId, hidePageHeadSection } = props

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const params = useParams()

    const [activeTab, setActiveTab] = useState<string>("details")
    const [availableTabs, setAvailableTabs] = useState<string[]>([
        "details",
        "report",
        "resolved"
    ])

    const [issue, setIssue] = useState<IIssuesModal>(EmptyIssueModel)
    const [basicDataViewerItems, setBasicDataViewerItems] = useState<Array<IBasicDataViewItem>>([])

    const [basicReportedDetails, setReportedDetails] = useState<Array<IBasicDataViewItem>>([])
    const [basicResolvedDetails, setResolvedDetails] = useState<Array<IBasicDataViewItem>>([])

    const issueId = customId ? customId : params.id ? Number(params.id) : 0

    const [notFound, setNotFound] = useState<boolean>(false)
    const [undoTimeout, setUndoTimeout] = useState<NodeJS.Timeout | null>(null)

    const uploadLoader = (status: boolean) => dispatch(updateGlobalLoaderStatus({ status: status }))

    const currentPage = usePageDetails("issues")

    const issuesApp = new IssuesApp()

    /**
     * Load item
     */
    const loadItem = () => {
        uploadLoader(true)

        issuesApp.singleInstanceDetails(issueId)
            .then(response => {

                const result: IIssuesModal = response.data
                setIssue(result)


                setNotFound(false)
                uploadLoader(false)

            })
            .catch(error => {

                const { status } = error.response

                handleRequestError(error, dispatch)

                if (status == 404){
                    setNotFound(true)
                }

                uploadLoader(false)

            })
    }
 

    /**
     * Handle before delete
     */
    const handleBeforeDelete = () => {
        setNotFound(false)
        dispatch(updateGlobalLoaderStatus({ status: false }))

        if (undoTimeout){
            clearTimeout(undoTimeout)
        }
    }

    /**
     * Delete item
     */
    const handleDeleteItem = () => {
    
        dispatch(updateGlobalLoaderStatus({ status: true }))

        setNotFound(true)

        updateQuickNotificationDetails(true, "Issue has been deleted", "error", dispatch, () => {}, 5000, false, [
            // {
            //     label: "Close",
            //     handleClose: true
            // },
            {
                label: "Undo",
                handleClose: false,
                onClick: () => handleBeforeDelete()
            }
        ])

        const timeout = setTimeout(() => {
            dispatch(updateGlobalLoaderStatus({ status: false }))
            issuesApp.deleteInstance(issue.id)
                .then(response => {
                    navigate("/panel/" + currentPage.name)
                })
                .catch(error => {
                    handleRequestError(error, dispatch)
                })
        }, 5000);
    
        setUndoTimeout(timeout);

    }


    /**
     * Load item
     */
    useEffect(() => {
        loadItem()
    }, [issueId])

    /**
     * Update columns
     */
    useEffect(() => {

        setBasicDataViewerItems([
            {
                type: "text",
                label: "Item",
                value: issue.item_title
            },
            {
                type: "status",
                label: "Status",
                value: issue.status
            },
            {
                type: "status",
                label: "Type",
                value: issue.issue_type
            },
            {
                type: "text",
                label: "Items Quantity",
                value: `${issue.quantity}`
            },
            {
                type: "text",
                label: "Origin",
                value: "",
                customContent: () => {

                    if (issue.warehouse){
                        return(
                            <div className="request-designtaions-container item-current-location">
                                <div className="content-of-item-current-location">
                
                                    <div className="item-single-current-location">
                                        <div className="content-of-item-single-current-location">
                                            <h4>{issue.warehouse}</h4>
                                        </div>
                                    </div>
                
                                    <GenerateIcon icon="arrow-right" />
                
                                    <div className="item-single-current-location">
                                        <div className="content-of-item-single-current-location">
                                            <h4>{issue.section}</h4>
                                        </div>
                                    </div>
                
                                </div>
                            </div>
                        )
                    }

                    if (issue.location){
                        return(
                            <div className="request-designtaions-container item-current-location">
                                <div className="content-of-item-current-location">
                
                                    <div className="item-single-current-location">
                                        <div className="content-of-item-single-current-location">
                                            <h4>{issue.location}</h4>
                                        </div>
                                    </div>
                
                                </div>
                            </div>
                        )
                    }

                    return <p>-</p>
                }
            },
            {
                type: "text",
                label: "Request No.",
                value: issue.request_ref ? issue.request_ref : "-",
                customContent() {
                    if (!issue.request_ref){
                        return <p>-</p>
                    }

                    return <a href={"/requests/" + issue.request_ref} target='_blank'>{issue.request_ref}</a>
                },
            },
            {
                type: "text",
                label: "Caused by",
                value: issue.caused_by_name
            },
        ])

        setReportedDetails([
            {
                type: "text",
                label: "Reported by",
                value: issue.reported_by_title
            },
            {
                type: "text",
                label: "Caused by",
                value: issue.caused_by_name
            },
            {
                type: "date",
                label: "Reported on",
                value: issue.timestamp,
            },
            {
                type: "text",
                label: "Description",
                value: "",
                customContent: () => {
                    if (issue.description_text){
                        return <p className="pre-line">{issue.description_text}</p>
                    }

                    if (issue.description){
                        return <RenderBlockEditor blocks={(issue.description as unknown as IBlock[])} />
                    }

                    return <></>
                },
                displayLabelNextLine: true
            },
            {
                type: "text",
                label: "Images",
                value: "",
                customContent: () => {

                    return(
                        <div className="issue-attachments-box">
                            <Attachments label="Imges" attachments={issue.issue_images} setAttachments={() => {}} disableUpload disableDelete />
                        </div>
                    )
                },
                displayLabelNextLine: true
            },
        ])

        setResolvedDetails([
            {
                type: "text",
                label: "Resolved by",
                value: issue.resolved_by_title
            },
            {
                type: "date",
                label: "Resolved on",
                value: issue.resolved_at,
            },
            {
                type: "text",
                label: "Description",
                value: "",
                customContent: () => {
                    if (issue.resolved_text){
                        return <p className="pre-line">{issue.resolved_text}</p>
                    }

                    if (issue.resolved_description){
                        return <RenderBlockEditor blocks={(issue.resolved_description as unknown as IBlock[])} />
                    }

                    return <></>
                },
                displayLabelNextLine: true
            },
            {
                type: "text",
                label: "Images",
                value: "",
                customContent: () => {

                    return(
                        <div className="issue-attachments-box">
                            <Attachments label="Imges" attachments={issue.resolved_images} setAttachments={() => {}} disableUpload disableDelete />
                        </div>
                    )
                },
                displayLabelNextLine: true
            },
        ])

    }, [issue])

    /**
     * Issue details
     */
    const renderIssueDetails = () => {
        return(
            <SectionBoxContainer showTitle title="Details" description="Here are the issue details">
                <BasicDataViewer items={basicDataViewerItems} />
            </SectionBoxContainer>
        )
    }

    /**
     * Report details
     */
    const renderReportDetails = () => {
        return(
            <SectionBoxContainer showTitle title="Report Details">
                <BasicDataViewer items={basicReportedDetails} />
            </SectionBoxContainer>
        )
    }

    /**
     * Resolve details
     */
    const renderResolvedDetails = () => {

        if (!issue.resolved){
            return(
                <SectionBoxContainer showTitle title="Resolution Details">
                    <AlertElement type="info" message="The issue hasn't been resolved yet." />
                </SectionBoxContainer>
            )
        }

        return(
            <SectionBoxContainer showTitle title="Resolution Details">
                <BasicDataViewer items={basicResolvedDetails} />
            </SectionBoxContainer>
        )
    }

    return(
        <div>
           {!hidePageHeadSection && <PageHeadSection 
                pageName={currentPage.name} 
                customTitle={notFound ? "404" : "View Issue"} 
                customActionButtons={[
                    {
                        icon: "delete",
                        variant: "text",
                        label: "Delete",
                        color: "error",
                        onClick: () => handleDeleteItem()
                    },
                    {
                        icon: "done",
                        variant: "text",
                        link: replaceItemDataInLink(`/panel/${currentPage.name}/edit/[id]`, issue),
                        label: "Resolve",
                        color: "success",
                        disabled: issue.resolved,
                    }
                ]}
            />}

            {notFound && <SectionBoxContainer><NotFound message={issuesApp.dataDisplayer.notionFound.message} /></SectionBoxContainer>}
            {!notFound && (
                <div className="admin-single-item-view-page-container">
                    <div className="content-of-admin-single-item-view-page-container">

                        <Tabs 
                            activeTab={activeTab}
                            availableTabs={availableTabs}
                            updateActivateTab={(name) => setActiveTab(name)}
                            tabs={[
                                {
                                    name: "details",
                                    icon: "list",
                                    title: "Details",
                                    component: renderIssueDetails
                                },
                                {
                                    name: "report",
                                    icon: "mail",
                                    title: "Report Details",
                                    component: renderReportDetails
                                },
                                {
                                    name: "resolved",
                                    icon: "done",
                                    title: "Resolution Details",
                                    component: renderResolvedDetails
                                }
                            ]}
                        />

                    </div>
                </div>
            )}

        </div>
    )
}